import { normalizeHex } from './brand-color';

export type PublicThemeMode = 'light' | 'dark' | 'auto';

/**
 * The public branding as the shell reads it (spec §9.2). The BFF's `ApplicantBranding` grows
 * these fields in parallel, so every field beyond `name` may be missing and degrades quietly.
 */
export interface PublicBranding {
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
  themeMode: PublicThemeMode;
  brandHeaderBackground: boolean;
  contactEmail: string | null;
  contactPhone: string | null;
}

export const EMPTY_PUBLIC_BRANDING: PublicBranding = {
  name: '',
  logoUrl: null,
  primaryColor: null,
  themeMode: 'light',
  brandHeaderBackground: false,
  contactEmail: null,
  contactPhone: null,
};

const text = (value: unknown, max: number) =>
  typeof value === 'string' && value.trim() && value.trim().length <= max ? value.trim() : null;

/** Maps settings' legacy `system` to `auto`; anything unknown falls back to light. */
export function normalizeThemeMode(value: unknown): PublicThemeMode {
  if (value === 'dark' || value === 'auto') return value;
  if (value === 'system') return 'auto';
  return 'light';
}

function safeLogoUrl(value: unknown): string | null {
  const url = text(value, 2048);
  if (!url) return null;
  if (/^blob:/i.test(url) || /^data:image\//i.test(url) || url.startsWith('/')) return url;
  try {
    return new URL(url).protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

function safePhone(value: unknown): string | null {
  const phone = text(value, 32);
  return phone && /^[0-9+().\- ]+$/.test(phone) && /\d/.test(phone) ? phone : null;
}

function safeEmail(value: unknown): string | null {
  const email = text(value, 254);
  return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

export function normalizePublicBranding(raw: unknown): PublicBranding {
  if (!raw || typeof raw !== 'object') return EMPTY_PUBLIC_BRANDING;
  const value = raw as Record<string, unknown>;
  return {
    name: text(value.name, 200) ?? '',
    logoUrl: safeLogoUrl(value.logoUrl),
    primaryColor: normalizeHex(typeof value.primaryColor === 'string' ? value.primaryColor : null),
    themeMode: normalizeThemeMode(value.themeMode),
    brandHeaderBackground: value.brandHeaderBackground === true,
    contactEmail: safeEmail(value.contactEmail),
    contactPhone: safePhone(value.contactPhone),
  };
}

/** `tel:` keeps the digits and a leading plus only. */
export function telHref(phone: string): string {
  const digits = phone.replace(/[^\d+]/g, '');
  return `tel:${digits.startsWith('+') ? `+${digits.slice(1).replace(/\+/g, '')}` : digits.replace(/\+/g, '')}`;
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase();
}
